/**
 * Return-to-Training Plan
 *
 * When a user comes back after a 14+ day break, jumping straight back into
 * the program where they left off is the wrong move. This module computes
 * a calibrated re-entry plan:
 *
 *   - How much fitness has decayed (Banister model: ~15-20 day half-life)
 *   - What intensity to start at (% of pre-break working sets)
 *   - How many ramp days/weeks before they're back to full effort
 *   - Where in their program to resume (rewind if needed)
 *   - What to expect (soreness, fatigue, etc.)
 *
 * The plan is structured data — the UI renders it, the user accepts or
 * tweaks. Pure function: no I/O, no side effects, fully unit-testable.
 *
 * Evidence base for the brackets:
 *   - Bosquet 2013 meta-analysis (Scand J Med Sci Sports): negligible 1RM
 *     loss at ≤14d; 5-10% at 2-4 wk; ~14.5% at 8 wk
 *   - Mujika & Padilla 2000/2001 detraining reviews: VO2max drops ~7% at
 *     4 wk, ~20% at 8 wk — aerobic decays ~2× faster than strength
 *   - Damas 2018: eccentric tolerance materially blunted past 21d off
 *   - Seaborne 2018 / Psilander 2019: muscle memory cuts regain time ~50%
 *   - Caterisano 2019 (NSCA Strength Cond J): 50-70% prior load week 1
 *     after >2 wk off, 5-10%/wk progression
 *   - Bompa Periodization 6e + NSCA TSAC: 2-4 wk anatomical-adaptation
 *     phase after extended layoffs
 *   - Correa 2016, Fragala 2019: older adults (60+) detrain ~1.5-2× faster
 */

import type { ReentryState } from './reentryState';

export interface ReturnPlan {
  /** A short status banner: "16 days off — moderate detraining." */
  summary: string;

  /** Estimated fitness retained, 0-100% (100 = no decay, 0 = full reset). */
  fitnessRetained: number;

  /** Suggested working-set intensity for week 1, as a percentage of pre-break loads. */
  week1IntensityPct: number;

  /**
   * Suggested cap for aerobic/cardio intensity in week 1. VO2max detrains
   * ~2× faster than 1RM (Mujika & Padilla), so we cap conditioning more
   * aggressively than lifting.
   */
  week1AerobicIntensityPct: number;

  /** Number of ramp weeks before full intensity returns. */
  rampWeeks: number;

  /** Whether to suggest rewinding the program by N days to ease back in. */
  programRewindDays: number;

  /** Numbered actionable steps the user can take this week. */
  actions: string[];

  /** Honest expectations — soreness, fatigue, energy. */
  expectations: string[];

  /** Confidence in the plan (0-1). Falls below 0.6 when we don't have a fitness baseline. */
  confidence: number;

  /**
   * Age modifier actually applied to rampWeeks. 1.0 = none, 1.2 = 40-55
   * interpolation, 1.5 = 55+. Surfaced so the UI can explain "longer ramp
   * because of age" without the user wondering why.
   */
  ageMultiplier: number;
}

const FITNESS_HALF_LIFE_DAYS = 17; // midpoint of Banister 15-20d range

/**
 * Compute the share of fitness retained after `days` away.
 *
 * Math: Banister fitness component decays with half-life ~17 days.
 *   retained = 0.5 ^ (days / half_life)
 *
 * - 7 days off  => ~75% retained
 * - 14 days off => ~56%
 * - 21 days off => ~42%
 * - 30 days off => ~30%
 * - 60 days off => ~9%
 */
export function fitnessRetainedAfter(days: number): number {
  if (days <= 0) return 100;
  const fraction = Math.pow(0.5, days / FITNESS_HALF_LIFE_DAYS);
  return Math.round(fraction * 100);
}

/**
 * Age multiplier on ramp duration.
 *
 * Correa 2016, Sayers 2020: older adults lose strength ~1.5-2× faster
 * during detraining. Fragala 2019 (NSCA position stand for 50+) recommends
 * 3-4 wk reconditioning vs. 1-2 wk in younger populations. 40-55 is sparse
 * in the literature — we interpolate.
 *
 *   <40   → 1.0 (no penalty)
 *   40-55 → 1.2 (interpolated, flag in UI)
 *   55+   → 1.5
 */
export function ageMultiplier(age: number | null | undefined): number {
  if (!age || age < 40) return 1.0;
  if (age < 55) return 1.2;
  return 1.5;
}

/**
 * Build a structured return-to-training plan from the user's reentry state.
 *
 * Returns null when status is not 'returning' — the plan only applies to
 * 14+ day gaps. Shorter gaps don't need a recalibration; they just need a
 * normal session.
 *
 * @param state    Computed reentry classification (days off, missed habits)
 * @param age      Optional user age — applied as a ramp-week multiplier
 *                 (Correa, Fragala). Omit for unknown.
 */
export function buildReturnPlan(state: ReentryState, age?: number | null): ReturnPlan | null {
  if (state.status !== 'returning') return null;

  const days = Math.min(state.daysSinceLastSession, 365); // cap for stability
  const fitnessRetained = fitnessRetainedAfter(days);

  // Strength intensity prescription. Curve hugs the literature:
  //   14d off (retained ~56) → 72%   (matches Bosquet 5-10% 1RM loss at 2-4 wk)
  //   28d off (retained ~32) → 63%   (matches NSCA 65-75% week 1 prescription)
  //   60d off (retained ~9)  → 54%   (matches Caterisano 50-70% range)
  //   90d+ off               → 50%   (NSCA TSAC + Bompa anatomical adaptation floor)
  const week1IntensityPct = Math.max(50, Math.min(85, Math.round(50 + fitnessRetained * 0.4)));

  // Aerobic cap. VO2max detrains ~2× faster than 1RM (Mujika & Padilla,
  // Pedlar, Sousa). Applying a 0.85 multiplier on the strength cap is a
  // pragmatic translation — if strength caps at 70%, cardio caps at ~60%.
  // Floor at 45% so beginners still get a stimulus.
  const week1AerobicIntensityPct = Math.max(45, Math.round(week1IntensityPct * 0.85));

  // Ramp length brackets — evidence-tuned.
  //   ≤14d:  1 wk    Bosquet meta says negligible 1RM loss; just shake off rust
  //   15-28: 2 wk    Damas 2018 — eccentric tolerance returns within ~2 wk
  //   29-60: 3 wk    NSCA + Bompa anatomical-adaptation 2-4 wk; pick 3 as floor
  //   60-89: 4 wk    Bompa "transition-to-AA" upper bound
  //   90+:   5 wk    Match Fragala / NSCA TSAC reconditioning for substantial layoffs
  let baseRampWeeks: number;
  if (days <= 14) baseRampWeeks = 1;
  else if (days <= 28) baseRampWeeks = 2;
  else if (days <= 60) baseRampWeeks = 3;
  else if (days <= 89) baseRampWeeks = 4;
  else baseRampWeeks = 5;

  // Apply age multiplier — older adults detrain faster + reaccumulate
  // capacity more slowly.
  const ageMult = ageMultiplier(age);
  const rampWeeks = Math.max(1, Math.round(baseRampWeeks * ageMult));

  // Program rewind — keep current cutoffs; the rewind is about landing at
  // the top of a microcycle, not about detraining math.
  const programRewindDays = days < 21 ? 0 : days < 60 ? 7 : 14;

  // Build the actionable steps and expectations.
  const actions: string[] = [];
  const expectations: string[] = [];

  if (programRewindDays > 0) {
    actions.push(`Rewind your program by ${programRewindDays} days so you start at the top of a training week.`);
  }

  actions.push(
    `First session: cap working sets at ${week1IntensityPct}% of your pre-break load. Hit form, not numbers.`,
  );

  actions.push(
    `Aerobic / ruck / cardio: cap at ${week1AerobicIntensityPct}% of pre-break pace — match volume, don't push intensity (aerobic capacity decays roughly twice as fast as strength).`,
  );

  if (state.missedRecovery) {
    actions.push('Log a recovery score today, even if approximate. The system needs a baseline.');
  }
  if (state.missedNutrition) {
    actions.push('Restart nutrition logging — even one meal/day rebuilds the streak signal.');
  }

  if (rampWeeks >= 3) {
    actions.push(`Plan ${rampWeeks - 1} ramp weeks before returning to full intensity. The strength comes back faster than the size on the regain curve — don't chase the mirror.`);
  }

  if (ageMult > 1.0) {
    actions.push(
      ageMult >= 1.5
        ? `Ramp extended for age — recovery between sessions is the rate-limiter, not the working sets. Take the extra day if anything feels off.`
        : `Ramp extended modestly for age. Listen to joint signal more than muscle signal during week 1.`,
    );
  }

  // Honest expectations — these set the user up to not bail when soreness hits.
  if (days <= 14) {
    expectations.push('Light DOMS for 1-2 days. CNS pattern memory is intact at this gap — loads will feel close to where you left them.');
  } else if (days <= 28) {
    expectations.push('Soreness for 3-5 days, peaks 48h after the first session. Working sets that used to feel medium will feel hard — the rust comes off in 2-3 sessions.');
  } else if (days <= 60) {
    expectations.push('Soreness for 5-7 days. Eccentric tolerance has dropped — expect harder DOMS than memory suggests. Strength returns in 2-4 sessions, size lags.');
  } else {
    expectations.push('Significant soreness for 5-7+ days, possibly across multiple sessions. Don\'t panic — muscle memory accelerates the regain. Expect ~80% of pre-break strength back inside 2 weeks; rest by week 4-6.');
  }

  if (days >= 28) {
    expectations.push('Size lags strength on the return curve. The mirror will catch up by week 4-6 — don\'t bail when it doesn\'t track day 1.');
  }

  // Confidence: falls when we have no fitness baseline (low session history).
  const confidence = days < 60 ? 0.85 : 0.7;

  // Summary line — short status badge for the banner header.
  const baseSummary =
    days <= 14 ? `${days} days off — minimal detraining, 1-week ramp.` :
    days <= 28 ? `${days} days off — light detraining, 2-week ramp.` :
    days <= 60 ? `${days} days off — moderate detraining, 3-week ramp.` :
    days <= 89 ? `${days} days off — significant detraining, 4-week ramp.` :
    `${days} days off — substantial reset. Treat the next ${rampWeeks} weeks as a re-entry block.`;
  const summary = ageMult > 1.0
    ? baseSummary.replace(/(\d+)-week ramp/, `${rampWeeks}-week ramp`)
    : baseSummary;

  return {
    summary,
    fitnessRetained,
    week1IntensityPct,
    week1AerobicIntensityPct,
    rampWeeks,
    programRewindDays,
    actions,
    expectations,
    confidence,
    ageMultiplier: ageMult,
  };
}
